// ./src/components/features/VehicleTasks/VehicleTaskList/VehicleTaskList.tsx
//
// List component for the VehicleTasks page.
// Renders tasks as cards grouped by status with priority indicators,
// completion checkboxes, and Framer Motion entrance animations.
//
// Context support:
// - When rendered inside a <VehicleTasksProvider>, data props (tasks, loading,
//   loadingMore, hasMore, onLoadMore, deletingId, togglingTaskId) are read
//   from context automatically. Props can still override context values.
// - When rendered outside a provider, all data must be passed via props.
//
// Design decisions:
// - Custom TaskCard with checkbox, priority borders, status footer actions
// - Grouped by status (In Progress → To Do → Complete) with collapsible sections
// - SwipeableCard actions on mobile, inline actions on desktop
// - Staggered entrance animation on first load (via useEntranceAnimation)

import React, { useState, useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Center,
  Badge,
  ThemeIcon,
  UnstyledButton,
  Tooltip,
  Skeleton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(relativeTime);

import {
  VEHICLE_TASK_SCHEDULE_TYPE_MAP,
  getTaskStatusOption,
  getTaskPriorityOption,
  getTaskCategoryOption,
} from "@/config/vehicleTaskOptions";

import {
  VehicleTask,
  VEHICLE_TASK_STATUS,
  VEHICLE_TASK_PRIORITY,
  isTaskOverdue,
  isRecurringTask,
} from "@/utils/api/VehicleTaskService";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import { useOptionalVehicleTasksContext } from "@/contexts/VehicleTasksContext";

import { AppButton } from "@/components/generic/AppButton";
import { AppIcon } from "@/components/generic/AppIcon";
import { EmptyState } from "@/components/generic/EmptyState";
import { LoadMoreButton } from "@/components/generic/LoadMoreButton";
import { SwipeableCard, SwipeAction, useSwipeableList } from "@/components/generic/SwipeableCard";

// =============================================================================
// Types
// =============================================================================

export interface VehicleTaskListProps {
  /**
   * Array of vehicle task records to display.
   * Optional when inside a VehicleTasksProvider — reads from context.
   */
  tasks?: VehicleTask[];

  /**
   * Loading state for initial load.
   * Optional when inside a VehicleTasksProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditTask?: (task: VehicleTask) => void;

  /** Callback when delete action is clicked */
  onDeleteTask?: (task: VehicleTask) => void;

  /** Callback when status change is requested */
  onStatusChange?: (task: VehicleTask, newStatus: number) => void;

  /** Callback when complete is clicked (dedicated mutation) */
  onCompleteTask?: (task: VehicleTask) => void;

  /** Callback when a task card is clicked (open detail) */
  onTaskClick?: (task: VehicleTask) => void;

  /** ID of task currently being deleted. Optional — reads from context. */
  deletingTaskId?: string | null;

  /** ID of task currently changing status */
  togglingTaskId?: string | null;

  /** ID of recently edited/added task to highlight */
  highlightedTaskId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddTask?: () => void;

  /** Whether to group tasks by status (default: true) */
  grouped?: boolean;

  /** Whether to show completed tasks (default: true) */
  showCompleted?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

/** Priority → left border color */
const PRIORITY_BORDER_COLORS: Record<number, string> = {
  [VEHICLE_TASK_PRIORITY.HIGH]: "var(--mantine-color-red-5)",
  [VEHICLE_TASK_PRIORITY.MEDIUM]: "var(--mantine-color-yellow-5)",
  [VEHICLE_TASK_PRIORITY.LOW]: "var(--mantine-color-gray-4)",
};

/** Status group display order */
const STATUS_GROUP_ORDER = [
  VEHICLE_TASK_STATUS.IN_PROGRESS,
  VEHICLE_TASK_STATUS.TODO,
  VEHICLE_TASK_STATUS.COMPLETE,
];

/** Status group icons + colors for section headers */
const STATUS_GROUP_CONFIG: Record<number, { icon: string; color: string }> = {
  [VEHICLE_TASK_STATUS.TODO]: { icon: "Circle", color: "gray" },
  [VEHICLE_TASK_STATUS.IN_PROGRESS]: { icon: "Clock", color: "blue" },
  [VEHICLE_TASK_STATUS.COMPLETE]: { icon: "CircleCheck", color: "teal" },
};

/** Default number of completed tasks to show before collapsing */
const COMPLETED_COLLAPSED_COUNT = 3;

// =============================================================================
// Animation Variants
// =============================================================================

const checkVariants = {
  unchecked: { scale: 1 },
  checked: {
    scale: [1, 1.3, 1],
    transition: { scale: { duration: 0.35, ease: "easeOut" as const } },
  },
};

const strikethroughVariants = {
  hidden: { width: "0%" },
  visible: {
    width: "100%",
    transition: { width: { duration: 0.3, ease: "easeOut" as const, delay: 0.1 } },
  },
};

// =============================================================================
// Helpers
// =============================================================================

function getDueDateDisplay(
  task: VehicleTask,
  t: (key: string, opts?: Record<string, unknown>) => string
): { text: string; color: string; isOverdue: boolean } | null {
  if (!task.dueDate) return null;

  const isComplete =
    task.status === VEHICLE_TASK_STATUS.COMPLETE ||
    task.status === VEHICLE_TASK_STATUS.REMOVED;

  // For completed/removed tasks, just show the formatted date
  if (isComplete) {
    return {
      text: dayjs.utc(task.dueDate).local().format("MMM DD, YYYY"),
      color: "dimmed",
      isOverdue: false,
    };
  }

  // Prefer server-computed daysUntilDue (calendar-day accurate)
  // Fall back to local calculation only when not available
  const daysUntilDue = task.daysUntilDue ?? calculateDaysUntilDue(task.dueDate);
  const overdue = task.isOverdue ?? daysUntilDue < 0;

  if (overdue) {
    const absDays = Math.abs(daysUntilDue);
    return {
      text: t("vehicleTasks:dueDate.overdue", { count: absDays }),
      color: "red",
      isOverdue: true,
    };
  }

  if (daysUntilDue === 0) {
    return {
      text: t("vehicleTasks:dueDate.today"),
      color: "orange",
      isOverdue: false,
    };
  }

  if (daysUntilDue === 1) {
    return {
      text: t("vehicleTasks:dueDate.tomorrow"),
      color: "yellow",
      isOverdue: false,
    };
  }

  if (daysUntilDue <= 7) {
    return {
      text: t("vehicleTasks:dueDate.inDays", { count: daysUntilDue }),
      color: "blue",
      isOverdue: false,
    };
  }

  return {
    text: dayjs.utc(task.dueDate).local().format("MMM DD, YYYY"),
    color: "dimmed",
    isOverdue: false,
  };
}

/**
 * Fallback: calculate days until due using calendar dates in local timezone.
 * Uses startOf('day') to compare calendar dates, not raw timestamps.
 */
function calculateDaysUntilDue(dueDate: string): number {
  const dueLocal = dayjs.utc(dueDate).local().startOf("day");
  const todayLocal = dayjs().startOf("day");
  return dueLocal.diff(todayLocal, "day");
}

/** Stop click propagation — used on the footer action bar so clicks don't trigger card onClick */
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

// =============================================================================
// Loading Skeletons
// =============================================================================

const TaskCardSkeleton: React.FC = () => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <Skeleton height={24} width={24} circle mt={2} />
      <Stack gap={8} style={{ flex: 1 }}>
        <Group justify="space-between" wrap="nowrap">
          <Skeleton height={18} width="60%" />
          <Skeleton height={20} width={60} radius="xl" />
        </Group>
        <Group gap="sm">
          <Skeleton height={14} width={80} />
          <Skeleton height={14} width={100} />
        </Group>
        <Skeleton height={14} width="40%" />
      </Stack>
    </Group>
  </Paper>
);

const LoadingSkeleton: React.FC = () => (
  <Stack gap="md">
    {/* Section header skeleton */}
    <Group gap={8}>
      <Skeleton height={20} width={20} circle />
      <Skeleton height={14} width={100} />
      <Skeleton height={18} width={18} circle />
    </Group>
    {Array.from({ length: 4 }).map((_, i) => (
      <TaskCardSkeleton key={i} />
    ))}
  </Stack>
);

// =============================================================================
// Status Checkbox Component
// =============================================================================

interface TaskCheckboxProps {
  status: number;
  priority: number;
  disabled?: boolean;
  onComplete?: () => void;
  onRevert?: () => void;
}

const TaskCheckboxComponent: React.FC<TaskCheckboxProps> = ({
  status,
  priority,
  disabled = false,
  onComplete,
  onRevert,
}) => {
  const { t } = useTranslation(["vehicleTasks"]);
  const isComplete = status === VEHICLE_TASK_STATUS.COMPLETE;
  const isInProgress = status === VEHICLE_TASK_STATUS.IN_PROGRESS;

  const priorityOption = getTaskPriorityOption(priority);
  const ringColor = isComplete
    ? "var(--mantine-color-teal-5)"
    : isInProgress
      ? "var(--mantine-color-blue-5)"
      : priorityOption?.color
        ? `var(--mantine-color-${priorityOption.color}-5)`
        : "var(--mantine-color-gray-4)";

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;

      if (isComplete) {
        onRevert?.();
      } else {
        onComplete?.();
      }
    },
    [disabled, isComplete, onComplete, onRevert]
  );

  const tooltipLabel = isComplete
    ? t("vehicleTasks:actions.reopen")
    : t("vehicleTasks:actions.markComplete");

  return (
    <Tooltip label={tooltipLabel} withArrow position="right" openDelay={400}>
      <UnstyledButton
        onClick={handleClick}
        disabled={disabled}
        style={{
          flexShrink: 0,
          width: 26,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
        }}
        aria-label={tooltipLabel}
      >
        <motion.div
          variants={checkVariants}
          animate={isComplete ? "checked" : "unchecked"}
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: `2px solid ${ringColor}`,
            backgroundColor: isComplete
              ? "var(--mantine-color-teal-5)"
              : isInProgress
                ? "var(--mantine-color-blue-0)"
                : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 200ms ease, border-color 200ms ease",
          }}
        >
          {isComplete && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, delay: 0.05 }}
            >
              <AppIcon iconName="Check" size={13} color="white" />
            </motion.div>
          )}
          {isInProgress && !isComplete && (
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "var(--mantine-color-blue-5)",
              }}
            />
          )}
        </motion.div>
      </UnstyledButton>
    </Tooltip>
  );
};

const TaskCheckbox = React.memo(TaskCheckboxComponent);

// =============================================================================
// Task Card Component
// =============================================================================

interface TaskCardProps {
  task: VehicleTask;
  onClick?: (task: VehicleTask) => void;
  onEdit?: (task: VehicleTask) => void;
  onDelete?: (task: VehicleTask) => void;
  onComplete?: (task: VehicleTask) => void;
  onStatusChange?: (task: VehicleTask, newStatus: number) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isToggling: boolean;
  skipAnimation?: boolean;
  // Swipe management props
  swipeable?: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TaskCardComponent: React.FC<TaskCardProps> = ({
  task,
  onEdit,
  onDelete,
  onComplete,
  onStatusChange,
  highlighted,
  isDeleting,
  isToggling,
  skipAnimation = false,
  swipeable = false,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["vehicleTasks", "common"]);

  const isComplete = task.status === VEHICLE_TASK_STATUS.COMPLETE;
  const isInProgress = task.status === VEHICLE_TASK_STATUS.IN_PROGRESS;
  const isTodo = task.status === VEHICLE_TASK_STATUS.TODO;
  const overdue = isTaskOverdue(task);
  const recurring = isRecurringTask(task);
  const isProcessing = isDeleting || isToggling;

  // Priority border color
  const borderLeftColor =
    PRIORITY_BORDER_COLORS[task.priority || VEHICLE_TASK_PRIORITY.LOW] ||
    PRIORITY_BORDER_COLORS[VEHICLE_TASK_PRIORITY.LOW];

  // Due date display
  const dueDateInfo = useMemo(
    () => getDueDateDisplay(task, t),
    [task.dueDate, task.status, t]
  );

  // Category info
  const categoryOption = useMemo(
    () => getTaskCategoryOption(task.category),
    [task.category]
  );

  // Priority info
  const priorityOption = useMemo(
    () => getTaskPriorityOption(task.priority),
    [task.priority]
  );

  // Schedule type info
  const scheduleOption = useMemo(
    () =>
      task.scheduleType
        ? VEHICLE_TASK_SCHEDULE_TYPE_MAP[task.scheduleType]
        : undefined,
    [task.scheduleType]
  );

  // Handle complete toggle
  const handleComplete = useCallback(() => {
    if (isProcessing) return;
    onComplete?.(task);
  }, [isProcessing, onComplete, task]);

  // Handle revert to todo
  const handleRevert = useCallback(() => {
    if (isProcessing) return;
    onStatusChange?.(task, VEHICLE_TASK_STATUS.TODO);
  }, [isProcessing, onStatusChange, task]);

  // Handle swipe state change
  const handleSwipeStateChange = useCallback(
    (isOpen: boolean) => onSwipeStateChange?.(isOpen),
    [onSwipeStateChange]
  );

  // Build swipe actions (mobile)
  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (!isComplete && onComplete) {
      actions.push({
        key: "complete",
        icon: "CircleCheck",
        label: t("vehicleTasks:actionsShort.complete"),
        color: "teal",
        onClick: () => onComplete(task),
        disabled: isProcessing,
        loading: isToggling,
      });
    }

    if (isTodo && onStatusChange) {
      actions.push({
        key: "inProgress",
        icon: "Clock",
        label: t("vehicleTasks:actionsShort.start"),
        color: "blue",
        onClick: () => onStatusChange(task, VEHICLE_TASK_STATUS.IN_PROGRESS),
        disabled: isProcessing,
      });
    }

    if (isComplete && onStatusChange) {
      actions.push({
        key: "reopen",
        icon: "RotateClockwise2",
        label: t("vehicleTasks:actionsShort.reopen"),
        color: "blue",
        onClick: () => onStatusChange(task, VEHICLE_TASK_STATUS.TODO),
        disabled: isProcessing,
      });
    }

    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(task),
        disabled: isProcessing,
      });
    }

    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(task),
        loading: isDeleting,
        disabled: isProcessing,
      });
    }

    return actions;
  }, [
    isComplete,
    isTodo,
    isProcessing,
    isToggling,
    isDeleting,
    onComplete,
    onStatusChange,
    onEdit,
    onDelete,
    task,
    t,
  ]);

  // ── Card content ──
  const cardContent = (
    <Paper
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 200ms ease",
        borderLeft: `3px solid ${isComplete ? "var(--mantine-color-teal-3)" : borderLeftColor}`,
        ...(highlighted
          ? {
            borderColor: "var(--mantine-color-teal-5)",
            boxShadow:
              "0 0 0 1px var(--mantine-color-teal-2), 0 4px 16px var(--mantine-color-teal-1)",
          }
          : {}),
        ...(overdue && !isComplete
          ? {
            boxShadow: "inset 0 0 0 1px var(--mantine-color-red-2)",
          }
          : {}),
      }}
    >
      <Group
        gap="md"
        wrap="nowrap"
        align="flex-start"
        p="md"
      >
        {/* ── Checkbox ── */}
        <TaskCheckbox
          status={task.status || VEHICLE_TASK_STATUS.TODO}
          priority={task.priority || VEHICLE_TASK_PRIORITY.LOW}
          disabled={isProcessing}
          onComplete={handleComplete}
          onRevert={handleRevert}
        />

        {/* ── Content ── */}
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Title + Priority badge */}
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Box style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Text
                size="md"
                fw={isComplete ? 400 : 600}
                c={isComplete ? "dimmed" : undefined}
                lineClamp={2}
              >
                {task.title}
              </Text>
              {/* Animated strikethrough for completed tasks */}
              {isComplete && (
                <motion.div
                  variants={strikethroughVariants}
                  initial={skipAnimation ? "visible" : "hidden"}
                  animate="visible"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    height: 1,
                    backgroundColor: "var(--mantine-color-gray-5)",
                    transformOrigin: "left",
                  }}
                />
              )}
            </Box>
            {priorityOption &&
              task.priority !== VEHICLE_TASK_PRIORITY.LOW && (
                <Tooltip
                  label={t(`vehicleTasks:${priorityOption.labelKey}`)}
                  withArrow
                  position="left"
                >
                  <Badge
                    size="xs"
                    variant="light"
                    color={priorityOption.color}
                    style={{ flexShrink: 0 }}
                    leftSection={
                      <AppIcon
                        iconName={priorityOption.icon || "ArrowUp"}
                        size={10}
                      />
                    }
                  >
                    {t(`vehicleTasks:${priorityOption.labelKey}`)}
                  </Badge>
                </Tooltip>
              )}
          </Group>

          {/* Row 2: Meta info chips */}
          <Group gap={8} wrap="wrap" style={{ rowGap: 4 }}>
            {/* Due date */}
            {dueDateInfo && (
              <Group gap={4} wrap="nowrap">
                <AppIcon
                  iconName={dueDateInfo.isOverdue ? "AlertCircle" : "Calendar"}
                  size={14}
                  color={dueDateInfo.color}
                />
                <Text
                  size="xs"
                  fw={dueDateInfo.isOverdue ? 600 : 400}
                  c={dueDateInfo.color}
                >
                  {dueDateInfo.text}
                </Text>
              </Group>
            )}

            {/* Category */}
            {categoryOption && (
              <Badge
                size="xs"
                variant="dot"
                color={categoryOption.color}
              >
                {t(`vehicleTasks:${categoryOption.labelKey}`)}
              </Badge>
            )}

            {/* Recurring indicator */}
            {recurring && scheduleOption && (
              <Tooltip
                label={t(`vehicleTasks:${scheduleOption.labelKey}`)}
                withArrow
              >
                <Group gap={3} wrap="nowrap" style={{ cursor: "help" }}>
                  <AppIcon iconName="Repeat" size={13} color="violet" />
                  <Text size="xs" c="violet">
                    {t(`vehicleTasks:${scheduleOption.labelKey}`)}
                  </Text>
                </Group>
              </Tooltip>
            )}

            {/* Vehicle */}
            {task.car && (
              <Group gap={4} wrap="nowrap">
                <AppIcon iconName="Car" size={13} color="gray" />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {task.car.label}
                </Text>
              </Group>
            )}

            {/* Assigned user */}
            {task.assignedToUser && (
              <Group gap={4} wrap="nowrap">
                <AppIcon iconName="User" size={13} color="gray" />
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {[task.assignedToUser.firstName, task.assignedToUser.lastName]
                    .filter(Boolean)
                    .join(" ")}
                </Text>
              </Group>
            )}

            {/* Linked expense indicator */}
            {task.linkedExpenseId && (
              <Tooltip
                label={t("vehicleTasks:list.linkedExpense")}
                withArrow
              >
                <Group gap={3} wrap="nowrap" style={{ cursor: "help" }}>
                  <AppIcon iconName="Receipt" size={13} color="teal" />
                  <Text size="xs" c="teal">
                    {t("vehicleTasks:list.expenseLogged")}
                  </Text>
                </Group>
              </Tooltip>
            )}
          </Group>

          {/* Row 3: Notes preview (if any) */}
          {task.notes && !isComplete && (
            <Text size="xs" c="dimmed" lineClamp={1} fs="italic">
              {task.notes}
            </Text>
          )}

          {/* Row 4: Completed info */}
          {isComplete && task.completedAt && (
            <Text size="xs" c="dimmed">
              {t("vehicleTasks:list.completedOn", {
                date: dayjs(task.completedAt).format("MMM DD, YYYY"),
              })}
              {task.completedByUser &&
                ` · ${[
                  task.completedByUser.firstName,
                  task.completedByUser.lastName,
                ]
                  .filter(Boolean)
                  .join(" ")}`}
            </Text>
          )}
        </Stack>
      </Group>

      {/* ── Desktop: Action buttons footer ── */}
      {!swipeable && (onEdit || onDelete || (!isComplete && onComplete)) && (
        <Box
          px="md"
          py={8}
          style={{
            borderTop: "1px solid var(--mantine-color-gray-1)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
          onClick={stopPropagation}
        >
          <Group justify="flex-end" gap={8}>
            {/* Status transitions */}
            {isTodo && onStatusChange && (
              <AppButton
                iconName="Clock"
                variant="secondary"
                size="xs"
                color="blue"
                onClick={() => onStatusChange(task, VEHICLE_TASK_STATUS.IN_PROGRESS)}
                disabled={isProcessing}
              >
                {t("vehicleTasks:actions.startProgress")}
              </AppButton>
            )}
            {isInProgress && onComplete && (
              <AppButton
                iconName="CircleCheck"
                variant="secondary"
                size="xs"
                color="teal"
                onClick={() => onComplete(task)}
                disabled={isProcessing}
                inProgress={isToggling}
              >
                {t("vehicleTasks:actions.markComplete")}
              </AppButton>
            )}
            {isComplete && onStatusChange && (
              <AppButton
                iconName="RotateClockwise2"
                variant="secondary"
                size="xs"
                color="blue"
                onClick={() => onStatusChange(task, VEHICLE_TASK_STATUS.TODO)}
                disabled={isProcessing}
              >
                {t("vehicleTasks:actions.reopen")}
              </AppButton>
            )}
            {onEdit && (
              <AppButton
                iconName="Edit"
                variant="secondary"
                size="xs"
                onClick={() => onEdit(task)}
                disabled={isProcessing}
              >
                {t("common:buttons.edit")}
              </AppButton>
            )}
            {onDelete && (
              <AppButton
                iconName="Trash"
                variant="secondary"
                size="xs"
                color="red"
                onClick={() => onDelete(task)}
                loading={isDeleting}
                disabled={isProcessing}
              >
                {t("common:buttons.delete")}
              </AppButton>
            )}
          </Group>
        </Box>
      )}
    </Paper>
  );

  // Wrap in SwipeableCard on mobile
  if (swipeable && leftSwipeActions.length > 0) {
    return (
      <SwipeableCard
        leftSwipeActions={leftSwipeActions}
        forceClose={forceClose}
        onForceCloseComplete={onForceCloseComplete}
        onSwipeStateChange={handleSwipeStateChange}
        disabled={isProcessing}
        borderRadius="var(--mantine-radius-md)"
      >
        {cardContent}
      </SwipeableCard>
    );
  }

  return cardContent;
};

const TaskCard = React.memo(TaskCardComponent);

// =============================================================================
// Section Header
// =============================================================================

interface StatusSectionHeaderProps {
  status: number;
  count: number;
  collapsed?: boolean;
  onToggle?: () => void;
}

const StatusSectionHeaderComponent: React.FC<StatusSectionHeaderProps> = ({
  status,
  count,
  collapsed,
  onToggle,
}) => {
  const { t } = useTranslation(["vehicleTasks"]);
  const config = STATUS_GROUP_CONFIG[status] || STATUS_GROUP_CONFIG[VEHICLE_TASK_STATUS.TODO];
  const statusOption = getTaskStatusOption(status);

  return (
    <UnstyledButton
      onClick={onToggle}
      style={{ width: "100%" }}
      aria-expanded={!collapsed}
    >
      <Group gap={8} wrap="nowrap" py={4}>
        <ThemeIcon variant="light" color={config.color} size="xs" radius="xl">
          <AppIcon iconName={config.icon} size={12} />
        </ThemeIcon>
        <Text size="sm" fw={600} c={config.color} tt="uppercase" lts={0.5}>
          {statusOption
            ? t(`vehicleTasks:${statusOption.labelKey}`)
            : String(status)}
        </Text>
        <Badge size="xs" variant="light" color={config.color} circle>
          {count}
        </Badge>
        <Box style={{ flex: 1 }} />
        {onToggle && (
          <motion.div
            animate={{ rotate: collapsed ? -90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <AppIcon iconName="ChevronDown" size={16} color="gray" />
          </motion.div>
        )}
      </Group>
    </UnstyledButton>
  );
};

const StatusSectionHeader = React.memo(StatusSectionHeaderComponent);

// =============================================================================
// Main Component
// =============================================================================

const VehicleTaskListComponent: React.FC<VehicleTaskListProps> = ({
  tasks: tasksProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditTask,
  onDeleteTask,
  onStatusChange,
  onCompleteTask,
  onTaskClick,
  deletingTaskId: deletingTaskIdProp,
  togglingTaskId,
  highlightedTaskId,
  onAddTask,
  grouped = true,
  showCompleted = true,
}) => {
  const { t } = useTranslation(["vehicleTasks", "common"]);

  // ── Context vs Props resolution ──
  // Props take priority so callers can override context values.
  const vehicleTasksContext = useOptionalVehicleTasksContext();

  const tasks = tasksProp ?? vehicleTasksContext?.tasks ?? [];
  const loading = loadingProp ?? vehicleTasksContext?.isLoading ?? false;
  const loadingMore = loadingMoreProp ?? vehicleTasksContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? vehicleTasksContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? vehicleTasksContext?.loadMore;
  const deletingTaskId = deletingTaskIdProp ?? vehicleTasksContext?.removingId ?? null;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation — fires once when data first arrives
  const { hasAnimated } = useEntranceAnimation({ itemCount: tasks.length });

  // Completed section collapse state
  const [completedCollapsed, setCompletedCollapsed] = useState(true);

  const handleSwipeStateChange = useCallback(
    (taskId: string, isOpen: boolean) => {
      if (isOpen) openCard(taskId);
    },
    [openCard]
  );

  // Group tasks by status
  const groupedTasks = useMemo(() => {
    const groups: Record<number, VehicleTask[]> = {
      [VEHICLE_TASK_STATUS.IN_PROGRESS]: [],
      [VEHICLE_TASK_STATUS.TODO]: [],
      [VEHICLE_TASK_STATUS.COMPLETE]: [],
    };

    for (const task of tasks) {
      const status = task.status || VEHICLE_TASK_STATUS.TODO;
      if (status === VEHICLE_TASK_STATUS.REMOVED) continue;
      if (groups[status]) {
        groups[status].push(task);
      } else {
        // Fallback: put unknown statuses in TODO
        groups[VEHICLE_TASK_STATUS.TODO].push(task);
      }
    }

    return groups;
  }, [tasks]);

  // Visible completed tasks (collapsed or expanded)
  const visibleCompletedTasks = useMemo(() => {
    const completed = groupedTasks[VEHICLE_TASK_STATUS.COMPLETE] || [];
    if (!completedCollapsed) return completed;
    return completed.slice(0, COMPLETED_COLLAPSED_COUNT);
  }, [groupedTasks, completedCollapsed]);

  const hiddenCompletedCount = useMemo(() => {
    const total = (groupedTasks[VEHICLE_TASK_STATUS.COMPLETE] || []).length;
    return Math.max(0, total - COMPLETED_COLLAPSED_COUNT);
  }, [groupedTasks]);

  // Non-completed tasks (for non-grouped view)
  const activeTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status !== VEHICLE_TASK_STATUS.COMPLETE &&
          t.status !== VEHICLE_TASK_STATUS.REMOVED
      ),
    [tasks]
  );

  // ── Row / card click ──

  const handleEdit = useCallback(
    (task: VehicleTask) => {
      closeAllCards();
      onEditTask?.(task);
    },
    [onEditTask, closeAllCards]
  );

  const handleDelete = useCallback(
    (task: VehicleTask) => {
      closeAllCards();
      onDeleteTask?.(task);
    },
    [onDeleteTask, closeAllCards]
  );

  const handleTaskClick = useCallback(
    (task: VehicleTask) => {
      closeAllCards();
      onTaskClick?.(task);
    },
    [onTaskClick, closeAllCards]
  );

  // Render a single task card
  const renderCard = useCallback(
    (task: VehicleTask, isMobile: boolean) => (
      <motion.div
        key={task.id}
        variants={hasAnimated ? undefined : entranceCardVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        exit="exit"
        layout
      >
        <TaskCard
          task={task}
          onClick={onTaskClick ? handleTaskClick : undefined}
          onEdit={onEditTask ? handleEdit : undefined}
          onDelete={onDeleteTask ? handleDelete : undefined}
          onComplete={onCompleteTask}
          onStatusChange={onStatusChange}
          highlighted={task.id === highlightedTaskId}
          isDeleting={deletingTaskId === task.id}
          isToggling={togglingTaskId === task.id}
          skipAnimation={hasAnimated}
          swipeable={isMobile}
          forceClose={
            isMobile ? shouldForceClose(task.id || "") : undefined
          }
          onForceCloseComplete={
            isMobile
              ? () => handleForceCloseComplete(task.id || "")
              : undefined
          }
          onSwipeStateChange={
            isMobile
              ? (isOpen) => handleSwipeStateChange(task.id || "", isOpen)
              : undefined
          }
        />
      </motion.div>
    ),
    [
      onTaskClick,
      onEditTask,
      onDeleteTask,
      onCompleteTask,
      onStatusChange,
      highlightedTaskId,
      deletingTaskId,
      togglingTaskId,
      hasAnimated,
      shouldForceClose,
      handleForceCloseComplete,
      handleSwipeStateChange,
      handleEdit,
      handleDelete,
      handleTaskClick,
    ]
  );

  // Render a status section (grouped view)
  const renderSection = useCallback(
    (status: number, sectionTasks: VehicleTask[], isMobile: boolean) => {
      if (sectionTasks.length === 0) return null;

      const isCompletedSection = status === VEHICLE_TASK_STATUS.COMPLETE;

      if (isCompletedSection && !showCompleted) return null;

      const tasksToRender = isCompletedSection
        ? visibleCompletedTasks
        : sectionTasks;

      return (
        <Stack gap="sm" key={status}>
          <StatusSectionHeader
            status={status}
            count={sectionTasks.length}
            collapsed={isCompletedSection ? completedCollapsed && hiddenCompletedCount > 0 : undefined}
            onToggle={
              isCompletedSection && hiddenCompletedCount > 0
                ? () => setCompletedCollapsed((v) => !v)
                : undefined
            }
          />
          <AnimatePresence mode="sync">
            {tasksToRender.map((task) => renderCard(task, isMobile))}
          </AnimatePresence>

          {/* Show more / less for completed */}
          {isCompletedSection && hiddenCompletedCount > 0 && (
            <Center>
              <UnstyledButton
                onClick={() => setCompletedCollapsed((v) => !v)}
              >
                <Text size="sm" c="blue" fw={500}>
                  {completedCollapsed
                    ? t("vehicleTasks:list.showMoreCompleted", {
                      count: hiddenCompletedCount,
                    })
                    : t("common:buttons.showLess")}
                </Text>
              </UnstyledButton>
            </Center>
          )}
        </Stack>
      );
    },
    [
      showCompleted,
      visibleCompletedTasks,
      completedCollapsed,
      hiddenCompletedCount,
      renderCard,
      t,
    ]
  );

  // Render grouped or flat content
  const renderContent = useCallback(
    (isMobile: boolean) => (
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-lg)",
        }}
      >
        {grouped ? (
          STATUS_GROUP_ORDER.map((status) =>
            renderSection(
              status,
              groupedTasks[status] || [],
              isMobile
            )
          )
        ) : (
          <AnimatePresence mode="sync">
            {activeTasks.map((task) => renderCard(task, isMobile))}
          </AnimatePresence>
        )}
      </motion.div>
    ),
    [
      hasAnimated,
      grouped,
      groupedTasks,
      activeTasks,
      renderSection,
      renderCard,
    ]
  );

  // Loading state
  if (loading && tasks.length === 0) {
    return <LoadingSkeleton />;
  }

  // Empty state
  if (!loading && tasks.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("vehicleTasks:emptyState.title")}
          description={t("vehicleTasks:emptyState.description")}
          iconName="ChecklistFilled"
          actionLabel={t("vehicleTasks:emptyState.actionLabel")}
          onAction={onAddTask}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Mobile: swipeable cards */}
      <Box hiddenFrom="md">
        {renderContent(true)}
      </Box>

      {/* Desktop: full-width cards with footer actions */}
      <Box visibleFrom="md">
        {renderContent(false)}
      </Box>

      {/* Load more — shared across breakpoints */}
      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const VehicleTaskList = React.memo(VehicleTaskListComponent);